import React, { ReactNode } from "react";

/**
 * Maps a tool name (single-property, per-child, bulk-chain, or multi-ticker bulk) to its action
 * category, so the approval renders the right message + summary for every variant. Without this,
 * names like "createChildPromotion" / "createBulkChildPromotions" fell through to the
 * generic "Confirma esta acción" with no summary card.
 *
 * `activateMultiple`/`deactivateMultiple`/`updateMultiple` are the MULTI-TICKER bulk tools —
 * SEVERAL DIFFERENT promotions on the SAME property toggled/edited in ONE approval. These are
 * distinct from the "Bulk"-named chain tools above (createBulkChildPromotions,
 * updateBulkChildPromotions), which instead fan ONE promotion out across several CHILD ENGINES —
 * hence the separate categories and summary components below. `updateChainMultiple` is a THIRD,
 * different shape again: SEVERAL DIFFERENT (ticker, child-scope) chain-update combinations in one
 * approval — each entry can target a different subset of children, and the SAME ticker can appear
 * more than once with a different scope (see MultiUpdateChainApprovalSummary).
 */
const TOOL_CATEGORIES = {
  create: ["createPromotion", "createChildPromotion", "createBulkChildPromotions"],
  update: ["updatePromotion", "updateChildPromotion", "updateBulkChildPromotions"],
  activate: ["activatePromotion", "activateChildPromotion"],
  deactivate: ["deactivatePromotion", "deactivateChildPromotion"],
  delete: ["deletePromotion", "deleteChildPromotion", "deleteBulkChildPromotions"],
  activateMultiple: ["activateMultiplePromotions"],
  deactivateMultiple: ["deactivateMultiplePromotions"],
  updateMultiple: ["updateMultiplePromotions"],
  updateChainMultiple: ["updateMultipleChildPromotions"],
} as const;

export type ToolCategory = keyof typeof TOOL_CATEGORIES | "generic";

export function categoryOf(toolName: string): ToolCategory {
  for (const [category, names] of Object.entries(TOOL_CATEGORIES)) {
    if ((names as readonly string[]).includes(toolName)) return category as ToolCategory;
  }
  return "generic";
}

/**
 * Section label used across every approval card. Matches the design system spec:
 * Open Sans 700, 12px / 15px, uppercase, -1% tracking, color #6A7896.
 */
export function MetaLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="uppercase font-bold text-[12px] leading-[15px] tracking-[-0.01em] text-[#6A7896]">
      {children}
    </span>
  );
}

/**
 * Card shell: white rounded container with a 1px #F5F5F5 divider between rows.
 * Exported so other widgets (e.g. WidgetObject) can render with the same visual
 * language as the approval cards instead of re-declaring the styles.
 */
export function ApprovalCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-[9px] bg-white my-3 overflow-hidden text-xs shadow-[0px_4px_8px_0px_#00000014] [&>*]:border-b [&>*]:border-[#F5F5F5] [&>*:last-child]:border-b-0">
      {children}
    </div>
  );
}

/**
 * Card header — #C6D5FF band with the promotion ticker (#212121) on the left and an
 * optional rate badge (#4D54D1 / white, radius 6px) on the right. `title` accepts any
 * node (not just plain text) so callers can render a linked ticker, as WidgetObject does.
 */
export function CardHeader({ title, badge }: { title: React.ReactNode; badge?: string }) {
  return (
    <div className="flex items-center justify-between gap-2 pl-3 pr-3 py-2.5 bg-[#C6D5FF]">
      <span className="font-bold text-[#212121] text-sm tracking-wide truncate">{title}</span>
      {badge && (
        <span className="flex-shrink-0 rounded-md bg-[#4D54D1] text-white px-2.5 py-1 font-semibold whitespace-nowrap">
          {badge}
        </span>
      )}
    </div>
  );
}

/** A single content row with its uppercase label and children below. */
export function Section({
  label,
  children,
}: {
  label?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5 pl-3 pr-4 py-3 bg-white">
      {label && <MetaLabel>{label}</MetaLabel>}
      {children}
    </div>
  );
}

/** Light state pill (e.g. "Inactiva", "No visible") with a leading icon. */
export function StatePill({ icon, children }: { icon: string; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md bg-[#F1F5FF] text-[#212121] px-3 py-1.5 font-medium">
      <em className={`fa ${icon} text-[#212121]`} aria-hidden />
      {children}
    </span>
  );
}

/**
 * A single changed-field row. `caption` renders a small secondary line below the value —
 * used e.g. by the inventory scope row to add the resulting line COUNT, the same way the
 * create card shows it via `approvalInventoryLines`.
 *
 * NOTE: pending PO spec, this only shows the NEW value (`after`) — the original ("before")
 * value is intentionally not rendered for now. `before` is still accepted so callers don't
 * need to change, it's just unused here.
 */
export function ChangeRow({
  field,
  before: _before,
  after,
  caption,
}: {
  field: string;
  before: ReactNode;
  after: ReactNode;
  caption?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5 min-w-0 pl-3 pr-4 py-3 bg-white">
      <MetaLabel>{field}</MetaLabel>
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-[#212121] text-sm break-words">{after}</span>
      </div>
      {caption && (
        <span className="text-[#949494] text-[14px] font-normal leading-none">{caption}</span>
      )}
    </div>
  );
}

/**
 * Generic collapsible disclosure for an "Afecta a X <thing>" legend followed by the full item
 * list — the same visual pattern already used by ChainAffectedEngines/GeolocationValue (a bold
 * intro sentence above a bordered toggle whose closed state shows the first item and whose
 * expanded state lists the rest). Factored out so device types, geolocation include/exclude, and
 * weekday restrictions (see DeviceTypesValue, GeolocationUpdateValue, WindowRangesView) all share
 * one implementation instead of hand-duplicating the `<details>` markup a 4th time.
 */
export function CollapsibleAffectedList({
  intro,
  items,
}: {
  intro: ReactNode;
  items: string[];
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[#212121] text-sm">{intro}</span>
      <details className="group w-full rounded-[10px] border border-[#E5E7EB] bg-white overflow-hidden">
        <summary className="flex h-[43px] items-center justify-between gap-2 px-4 cursor-pointer list-none text-[#212121] text-sm marker:content-none [&::-webkit-details-marker]:hidden">
          {items[0]}
          <em className="fa fa-chevron-down text-[13px] text-[#212121]" aria-hidden />
        </summary>
        <div className="flex flex-col gap-1 max-h-[168px] overflow-y-auto px-4 pb-3">
          {items.slice(1).map((item, i) => (
            <span key={`${item}-${i}`} className="text-[#212121] text-sm break-words">
              {item}
            </span>
          ))}
        </div>
      </details>
    </div>
  );
}
